import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";

// @ts-ignore
import "react-toastify/dist/ReactToastify.css";

import { ActivityButton, SequenceNumberInput } from "../../components/Common";
import {
  useGetFlashCardV2Query,
  useAddFlashCardV2Mutation,
  useUpdateFlashCardV2Mutation,
} from "../../store/api/flashCardV2Api";
import { ErrorHandlingTypeEnum, SequenceEnum } from "../../enums";
import { CreateFlashCardV2Request } from "../../interfaces/flash-card-v2";
import { ParsedError } from "../../interfaces";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  lessonId: string;
};
export const AddEditFlashCardV2Modal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  lessonId,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateFlashCardV2Request>();

  const [
    addFlashCardV2,
    {
      isLoading: isLoadingAdd,
      error: errorAdd,
      isError: isErrorAdd,
      isSuccess: isSuccessAdd,
    },
  ] = useAddFlashCardV2Mutation();
  const [
    updateFlashCardV2,
    {
      isLoading: isLoadingUpdate,
      error: errorUpdate,
      isError: isErrorUpdate,
      isSuccess: isSuccessUpdate,
    },
  ] = useUpdateFlashCardV2Mutation();

  const {
    data: flashCard,
    error: errorFlashCard,
    isLoading: isLoadingFlashCard,
    isError: isErrorFlashCard,
  } = useGetFlashCardV2Query(id as string, { skip: !id });

  //#region form
  const form = useFormik<CreateFlashCardV2Request>({
    enableReinitialize: true,
    initialValues: {
      content: request?.content || { en: "", ar: "" },
      sequenceNumber: request?.sequenceNumber || 0,
    },
    validationSchema: Yup.object({
      content: Yup.object({
        en: Yup.string()
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          )
          .max(200, ({ max }) =>
            t("maxLength", { maxLength: max, displayName: "englishValue" }),
          ),
        ar: Yup.string()
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          )
          .max(200, ({ max }) =>
            t("maxLength", { maxLength: max, displayName: "arabicValue" }),
          ),
      }),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", {
          displayName: "sequenceNumber",
        }),
      ),
    }),
    onSubmit: async (values) => {
      if (id) {
        await updateFlashCardV2({
          id,
          data: { ...values, sequenceNumber: Number(values.sequenceNumber) },
        });
      } else {
        await addFlashCardV2({
          lessonId,
          data: { ...values, sequenceNumber: Number(values.sequenceNumber) },
        });
      }
    },
  });
  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };
  //#endregion

  //#region effects

  useEffect(() => {
    if (id && flashCard) {
      form.setValues({
        content: flashCard.content,
        sequenceNumber:
          form.values.sequenceNumber || flashCard.sequenceNumber || 0,
      });
    }
  }, [flashCard, isLoadingFlashCard, id]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  useEffect(() => {
    if (
      (!isLoadingAdd && isSuccessAdd) ||
      (!isLoadingUpdate && isSuccessUpdate)
    ) {
      onClose();
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        className="modal-lg"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "flashCard" })
              : t("addItem", { item: "flashCard" })}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col xl="12">
                <SequenceNumberInput
                  tabIndex={1}
                  value={form.values.sequenceNumber}
                  onChange={(e) => {
                    e.preventDefault();
                    const { value } = e.target;
                    const regex =
                      /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                    if (regex.test(value.toString())) {
                      form.setFieldValue("sequenceNumber", value);
                    } else {
                      form.setFieldValue("sequenceNumber", "");
                    }
                  }}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.sequenceNumber && form.errors.sequenceNumber
                      ? true
                      : false
                  }
                  sequenceType={SequenceEnum.FLASH_CARD}
                  onGenerateSequenceNumber={(v: number) => {
                    form.setFieldValue("sequenceNumber", v);
                  }}
                  mode={id ? "edit" : "add"}
                  request={{
                    type: SequenceEnum.FLASH_CARD,
                    lessonId: lessonId,
                  }}
                />
                {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
                  <FormFeedback type="invalid">
                    {form.errors.sequenceNumber as string}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>

            <Row>
              <Col xl="12">
                <div className="mb-3">
                  <Label htmlFor="content.en" className="form-label">
                    {t("contentEn")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="textarea"
                    dir="ltr"
                    className="form-control"
                    id="content.en"
                    placeholder={t("english")}
                    value={form.values.content?.en || ""}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    invalid={
                      form.touched.content?.en && form.errors.content?.en
                        ? true
                        : false
                    }
                  />
                  {form.touched.content?.en && form.errors.content?.en ? (
                    <FormFeedback type="invalid">
                      {form.errors.content?.en}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
            </Row>
            <Row>
              <Col xl="12">
                <div className="mb-3">
                  <Label htmlFor="content.ar" className="form-label">
                    {t("contentAr")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="textarea"
                    dir="rtl"
                    className="form-control"
                    id="content.ar"
                    placeholder={t("arabic")}
                    value={form.values.content.ar || ""}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    invalid={
                      form.touched.content?.ar && form.errors.content?.ar
                        ? true
                        : false
                    }
                  />
                  {form.touched.content?.ar && form.errors.content?.ar ? (
                    <FormFeedback type="invalid">
                      {form.errors.content?.ar}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isLoadingAdd || isLoadingUpdate}
              disabled={isLoadingAdd || isLoadingUpdate || isErrorFlashCard}
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
